"use client"

import { useClickAway } from "ahooks"
import clsx from "clsx"
import { FC, ReactNode, useRef } from "react"
import { createPortal } from "react-dom"
import FocusLock from "react-focus-lock"
import { CloseButton } from "src/components/common/CloseButton"
import { ModalProvider, useModal } from "src/context/modal-context"
import { useBodyOverflowHidden } from "src/hooks/useBodyOverflowHidden"
import { useKeydown } from "src/hooks/useKeydown"

export type ModalProps = {
  close: () => void
  size?: "small" | "medium" | "large"
  children: ReactNode
}

export const Modal = ({ close, size = "medium", children }: ModalProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useClickAway(close, ref)
  useKeydown("Escape", close)
  useBodyOverflowHidden(true)

  const portalContainer = document.querySelector("body")!
  const portalChildren = (
    <FocusLock
      autoFocus
      returnFocus
      className="sm:min-size-full fixed inset-0 z-1 flex h-screen w-screen items-center justify-center overflow-y-auto bg-gray-900/50 text-center"
    >
      <div
        ref={ref}
        className={clsx(
          "flex h-fit w-full transform flex-col justify-start bg-white p-6 text-left align-middle shadow-xl sm:max-h-[75vh] sm:rounded-xl sm:border",
          {
            "max-w-md": size === "small",
            "max-w-xl": size === "medium",
            "max-w-3xl": size === "large",
          },
        )}
      >
        <ModalProvider close={close}>{children}</ModalProvider>
      </div>
    </FocusLock>
  )

  return <>{createPortal(portalChildren, portalContainer)}</>
}

const Title: FC<{ children: ReactNode }> = ({ children }) => {
  const { close } = useModal()

  return (
    <header className="flex items-center justify-between">
      <div className="text-base font-semibold">{children}</div>
      <CloseButton onClick={close} />
    </header>
  )
}

const Description: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <div className="text-default flex h-full items-center justify-center pt-2 pb-4 text-xs font-normal">{children}</div>
  )
}

const Body: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className="flex justify-center">{children}</div>
}

const Footer: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className="flex items-center justify-end gap-x-4">{children}</div>
}

Modal.Title = Title
Modal.Description = Description
Modal.Body = Body
Modal.Footer = Footer
